/**
 * ImpersonationConfirmGate — shared confirmation modal that wraps every
 * mutating action the admin can fire while impersonating a target user.
 *
 * Render-as-a-controlled-Dialog: parent owns the `open` state and
 * supplies the `verb` + `onConfirm` per mutation site. Copy template
 * is FIXED — only the verb varies — so the gate language is
 * recognizable across the 18 mutation surfaces (16 confirm-gated +
 * 2 Stripe-disabled).
 *
 * Copy:
 *   "You're acting as {email}. Confirm this {verb} on their account?"
 *
 * Usage from a mutation site:
 *   const { user } = useAuth();
 *   const [gateOpen, setGateOpen] = useState(false);
 *   ...
 *   const onSubmitClicked = () => {
 *     if (user?.is_impersonating) {
 *       setGateOpen(true); // gate first
 *     } else {
 *       reallySubmit();    // direct submit
 *     }
 *   };
 *   ...
 *   <ImpersonationConfirmGate
 *     open={gateOpen}
 *     onOpenChange={setGateOpen}
 *     verb="save these profile changes"
 *     onConfirm={reallySubmit}
 *   />
 *
 * Non-impersonating callers never instantiate this component, so
 * baseline (90%+) UX is untouched.
 */
import React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { ShieldAlert } from "lucide-react";

export default function ImpersonationConfirmGate({
  open,
  onOpenChange,
  verb,
  onConfirm,
}) {
  const { user } = useAuth();
  const targetEmail = user?.email || "this user";

  const handleConfirm = (e) => {
    e?.preventDefault?.();
    onOpenChange?.(false);
    // Defer to the next microtask so the dialog has time to start
    // closing before the underlying mutation kicks off (and possibly
    // navigates the user elsewhere).
    Promise.resolve().then(() => {
      try {
        onConfirm && onConfirm();
      } catch {
        /* surface in callsite — never throw from inside the gate */
      }
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="impersonation-confirm-gate">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-red-600">
            <ShieldAlert className="h-5 w-5" aria-hidden="true" />
            Admin action — impersonating
          </AlertDialogTitle>
          <AlertDialogDescription className="text-pp-text">
            You&rsquo;re acting as{" "}
            <strong
              className="font-semibold"
              data-testid="impersonation-confirm-gate-email"
            >
              {targetEmail}
            </strong>
            . Confirm this{" "}
            <strong
              className="font-semibold"
              data-testid="impersonation-confirm-gate-verb"
            >
              {verb}
            </strong>{" "}
            on their account?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="impersonation-confirm-cancel">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            className="bg-red-600 text-white hover:bg-red-700"
            data-testid="impersonation-confirm-accept"
          >
            Confirm
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * useImpersonationGate — small hook that returns
 *   - `gateState`: { open, verb }
 *   - `requestAction(verb, fn)`: if impersonating, opens the gate
 *     with that verb + stashes `fn`; otherwise invokes `fn`
 *     immediately. Caller renders `<ImpersonationConfirmGate>` with
 *     `gateProps` spread in.
 *
 * Keeps mutation sites lean — they don't need to wire `useState` +
 * `useAuth` + the gate JSX boilerplate; one hook call per page.
 */
export function useImpersonationGate() {
  const { user } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [verb, setVerb] = React.useState("");
  const pendingRef = React.useRef(null);

  const requestAction = React.useCallback(
    (nextVerb, fn) => {
      if (!user?.is_impersonating) {
        // Direct path — no gate, just run.
        try {
          fn();
        } catch {
          /* callsite handles its own errors */
        }
        return;
      }
      pendingRef.current = fn;
      setVerb(nextVerb);
      setOpen(true);
    },
    [user]
  );

  const onConfirm = React.useCallback(() => {
    const fn = pendingRef.current;
    pendingRef.current = null;
    if (fn) {
      try {
        fn();
      } catch {
        /* same — callsite handles its own errors */
      }
    }
  }, []);

  return {
    requestAction,
    gateProps: {
      open,
      onOpenChange: setOpen,
      verb,
      onConfirm,
    },
  };
}
